import m from '../m.js'
import { auth } from '../api.js'
import { onDismiss } from '../dismiss.js'
import { animateNextRender, playFlip } from '../flip.js'
import { icon } from '../icons.js'
import {
  DONE,
  filterQuery,
  filterValues,
  hasOpenPr,
  laneCards,
  matches,
  setFilter,
} from '../lanes.js'
import { LANE_WIDTHS, settings } from '../settings.js'
import { board } from '../store.js'
import { cardView } from './card.js'
import { detailView } from './detail.js'
import { avatarView, labelDotView, optionView, pickerView } from './widgets.js'

/**
 * Drag state shared by all lanes: dragged card, hovered lane, card to insert before (null = end),
 * where the pointer grabbed the card (the drop animates from where it was let go) and its height
 * (the space reserved where it would land).
 */
const dnd = {
  number: null,
  lane: null,
  before: null,
  grab: null,
  height: 0,
  reset() {
    Object.assign(dnd, {
      number: null,
      lane: null,
      before: null,
      grab: null,
      height: 0,
    })
    m.redraw()
  },
}

function canDrop(card, lane) {
  if (card.type !== 'pr') return true
  return lane !== DONE && card.state === 'open'
}

/**
 * Card number under the pointer's lower half, i.e. the card to insert before. Measured where
 * cards come to rest, not where a running slide shows them, so the slot does not flicker.
 */
function beforeAt(list, y) {
  for (const el of list.querySelectorAll('.card:not(.dragging)')) {
    const box = el.getBoundingClientRect()
    const top = box.top - new DOMMatrix(getComputedStyle(el).transform).m42
    if (y < top + box.height / 2) return Number(el.dataset.number)
  }
  return null
}

function newIssueView({ key, lane, onclose: onClose }) {
  return m(
    'form.new-issue',
    {
      key,
      onsubmit: evt => {
        evt.preventDefault()
        const input = evt.target.elements.title
        const title = input.value.trim()
        if (!title) return
        // the input stays open for the next issue; Esc closes it
        input.value = ''
        board.create(lane?.name, title)
      },
    },
    [
      m('input', {
        name: 'title',
        placeholder: m.t('lane.newIssuePlaceholder'),
        autocomplete: 'off',
        oncreate: ({ dom }) => {
          dom.focus()
          // see dismiss.js: a click outside or Esc closes only this input
          dom.undismiss = onDismiss({
            contains: target => dom.form.contains(target),
            close: onClose,
          })
        },
        onremove: ({ dom }) => dom.undismiss(),
      }),
    ]
  )
}

let addingTo = null

function laneView({ id, title, lane, cards, total, data }) {
  // evaluated live: dragover can fire before the redraw that follows dragstart
  const accepts = () =>
    Boolean(dnd.number && canDrop(board.card(dnd.number), id))
  const droppable = accepts()
  // space for the dragged card where it would land; the cards around slide aside (see flip.js)
  const indicator = key =>
    m.m('.drop-space', { key, vars: { height: `${dnd.height}px` } })
  const over = droppable && dnd.lane === id && id !== DONE
  const items = []
  if (addingTo === id)
    items.push(
      newIssueView({ key: 'new', lane, onclose: () => (addingTo = null) })
    )
  for (const card of cards) {
    if (over && dnd.before === card.number) items.push(indicator('drop'))
    items.push(cardView({ key: card.number, card, data, lane: id, dnd }))
  }
  if (over && dnd.before === null) items.push(indicator('drop'))

  return m.m(
    'section.lane',
    {
      key: id,
      cls: { droppable, over: droppable && dnd.lane === id },
      ondragover: evt => {
        if (!accepts()) {
          evt.redraw = false
          return
        }
        evt.preventDefault()
        evt.dataTransfer.dropEffect = 'move'
        const before =
          id === DONE ? null : beforeAt(evt.currentTarget, evt.clientY)
        if (dnd.lane === id && dnd.before === before)
          return (evt.redraw = false)
        animateNextRender()
        dnd.lane = id
        dnd.before = before
      },
      ondrop: evt => {
        evt.preventDefault()
        const { number, before, grab } = dnd
        const ok = accepts()
        if (ok)
          animateNextRender(
            grab && {
              [number]: new DOMRect(evt.clientX - grab.x, evt.clientY - grab.y),
            }
          )
        dnd.reset()
        if (ok) board.move(number, id, before)
      },
    },
    [
      m('header.lane-head', [
        m.m('span.lane-dot', {
          vars: { background: lane?.color && `#${lane.color}` },
          cls: { done: id === DONE },
        }),
        m(
          'h2',
          {
            title:
              id === DONE
                ? m.t('lane.doneHint', { days: data.config.done.days })
                : null,
          },
          title
        ),
        m.m(
          'span.count',
          {
            cls: { 'over-wip': lane?.wip && total > lane.wip },
            title: lane?.wip
              ? m.t('lane.wip', { wip: lane.wip })
              : m.t('lane.cards', total),
          },
          lane?.wip ? `${total}/${lane.wip}` : total
        ),
        m('.spacer'),
        lane &&
          data.repo.canTriage &&
          m(
            'button.icon-button',
            {
              title: m.t('lane.newIssue', { lane: title }),
              'aria-label': m.t('lane.newIssue', { lane: title }),
              onclick: () => (addingTo = addingTo === id ? null : id),
            },
            icon('plus')
          ),
      ]),
      m('.lane-cards', items),
    ]
  )
}

/** A filter as GitHub-style multi-select menu: "Assignee: (avatar) alice +1 ▾". */
function filterMenu(name, title, options) {
  const values = filterValues(name)
  const picked = options.filter(o => values.includes(o.value))
  return pickerView({
    className: values.length ? 'filter active' : 'filter',
    align: 'left',
    heading: m.t('filter.by', { name: title }),
    selected: values,
    options,
    onchange: selected => setFilter(name, selected.join(',')),
    toggle: [
      picked.length
        ? [
            m('span.muted', `${title}: `),
            optionView(picked[0]),
            picked.length > 1 && m('span.muted', `+${picked.length - 1}`),
          ]
        : title,
      icon('triangle-down'),
    ],
  })
}

function toolbarView({ data }) {
  const f = filterQuery()
  const person = user => ({
    value: user.login,
    label: user.login,
    view: () => [avatarView({ user }), user.login],
  })
  const me = auth.user && data.assignees.find(a => a.login === auth.user.login)
  return m('.toolbar', [
    m('label.search', [
      icon('search'),
      m('input', {
        type: 'search',
        placeholder: m.t('filter.search'),
        title: m.t('filter.searchHint'),
        value: f.q || '',
        oninput: evt => setFilter('q', evt.target.value),
      }),
    ]),
    filterMenu('type', m.t('filter.type'), [
      {
        value: 'issue',
        label: m.t('filter.issues'),
        view: () => [
          icon('issue-opened', { class: 'state open' }),
          m.t('filter.issues'),
        ],
      },
      {
        value: 'pr',
        label: m.t('filter.pulls'),
        view: () => [
          icon('git-pull-request', { class: 'state open' }),
          m.t('filter.pulls'),
        ],
      },
    ]),
    filterMenu('assignee', m.t('filter.assignee'), [
      {
        value: '-',
        label: m.t('filter.unassigned'),
        view: () => [icon('person'), m.t('filter.unassigned')],
      },
      ...(me ? [person(me)] : []),
      ...data.assignees.filter(a => a !== me).map(person),
    ]),
    filterMenu(
      'label',
      m.t('filter.label'),
      data.labels.map(l => ({
        value: l.name,
        label: l.name,
        view: () => [labelDotView({ color: l.color }), l.name],
      }))
    ),
    data.milestones.length > 0 &&
      filterMenu(
        'milestone',
        m.t('filter.milestone'),
        data.milestones.map(ms => ({
          value: ms.title,
          label: ms.title,
          view: () => [icon('milestone'), ms.title],
        }))
      ),
    Object.keys(f).length > 0 &&
      m(
        'button.invisible',
        {
          title: m.t('filter.clearHint'),
          onclick: () => m.route.set(`/${board.key}`, {}, { replace: true }),
        },
        [icon('x'), m.t('filter.clear')]
      ),
    m('.spacer'),
    board.loading &&
      board.data &&
      m('span.muted.updating', m.t('board.updating')),
    m.m(
      'span.live',
      {
        cls: { on: board.live },
        title: board.live ? m.t('live.onHint') : m.t('live.offHint'),
      },
      [icon('dot-fill'), board.live ? m.t('live.on') : m.t('live.off')]
    ),
  ])
}

export function boardView(attrs) {
  board.open(`${attrs.owner}/${attrs.repo}`)
  const data = board.data
  if (!data) {
    return m(
      '.center',
      board.error ? m('.error', board.error) : m('.muted', m.t('board.loading'))
    )
  }

  const filter = filterQuery()
  const { config } = data
  const lane = (id, title, def) => {
    const all = laneCards(data, id)
    return laneView({
      id,
      title,
      lane: def,
      total: all.length,
      cards: all.filter(
        c =>
          matches(c, filter) &&
          !(settings.hideIssuesWithPr && hasOpenPr(c, data.cards))
      ),
      data,
    })
  }

  return [
    toolbarView({ data }),
    data.configError &&
      m('.banner.warn', m.t('board.configError', { error: data.configError })),
    board.error &&
      m('.banner.warn', m.t('board.refreshFailed', { error: board.error })),
    m.m(
      '.lanes',
      {
        vars: {
          '--lane-width': `${LANE_WIDTHS[settings.laneWidth] || LANE_WIDTHS.normal}px`,
        },
        onRender: playFlip,
      },
      [
        ...config.lanes.map(def => lane(def.name, def.name, def)),
        lane(
          DONE,
          m.t('lane.done', { name: config.done.name, days: config.done.days }),
          null
        ),
      ]
    ),
    attrs.number
      ? m.keyed({ key: attrs.number }, [detailView(Number(attrs.number))])
      : null,
  ]
}
